import React, { useEffect, useState, useRef, useMemo } from "react";

import { NavLink, useLocation } from "react-router-dom";

import { getZoomData } from "../shared/_services/common.services";

import { useSnackbar } from "notistack";

import Loader from "./Loader";
import StarsIcon from "../styles/StarsIcon";
import { Button, Page, PageHead, Panel, cn } from "./ui";



function buildMeetingInfoFromLocationState(state) {

    const s = state ?? {};

    return {

        date: s.date ?? "",

        time: s.time ?? "",

        duration: s.duration ?? 0,

        userId: s.userId ?? "",

    };

}



function resolveMeetingsListUrl(userType, userId) {

    const base = "/meetings";

    if (!userId) {

        return base;

    }

    if (userType === "super-admin" || userType === "admin") {

        return `${base}/${userId}?isadmin=true`;

    }

    if (userType === "advisor") {

        return `${base}/${userId}`;

    }

    return base;

}



function applyAmPmToHours(hours, period) {

    if (period === "PM" && hours !== 12) {

        return hours + 12;

    }

    if (period === "AM" && hours === 12) {

        return 0;

    }

    return hours;

}



function formatMeetingDateTimeDisplay(meetingInfo) {

    const { date, time, duration } = meetingInfo;

    if (!date || !time || duration === 0) {

        return "Invalid Date/Time";

    }



    const startDate = new Date(date);

    const [timePart, period] = time.split(" ");

    let [hours, minutes] = timePart.split(":").map(Number);

    hours = applyAmPmToHours(hours, period);

    startDate.setHours(hours, minutes);



    const endDate = new Date(startDate);

    endDate.setMinutes(startDate.getMinutes() + duration);



    const dateOpts = { weekday: "long", year: "numeric", month: "long", day: "numeric" };

    const timeOpts = { hour: "numeric", minute: "numeric", hour12: true };

    const dateStr = startDate.toLocaleDateString("en-US", dateOpts);

    const startTimeStr = startDate.toLocaleTimeString("en-US", timeOpts);

    const endTimeStr = endDate.toLocaleTimeString("en-US", timeOpts);

    return `${dateStr}, ${startTimeStr} - ${endTimeStr}`;

}



function requestElementFullscreen(element) {

    if (!element) {

        return;

    }

    const request =

        element.requestFullscreen ||

        element.mozRequestFullScreen ||

        element.webkitRequestFullscreen ||

        element.msRequestFullscreen;

    if (request) {

        request.call(element);

    }

}



function triggerTextFileDownload(content, filename) {

    const blob = new Blob([content], { type: "text/plain" });

    const url = window.URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    anchor.href = url;

    anchor.download = filename;

    document.body.appendChild(anchor);

    anchor.click();

    window.URL.revokeObjectURL(url);

}



const MeetingDetails = () => {

    const location = useLocation();

    const meetingId = location?.state?.id;



    const [meetingDetails, setMeetingDetails] = useState("");

    const [videoUrl, setVideoUrl] = useState("");

    const notesRef = useRef(null);

    const { enqueueSnackbar } = useSnackbar();

    const [meetingInfo] = useState(() => buildMeetingInfoFromLocationState(location.state));



    const currentUser = JSON.parse(localStorage.getItem("currentUser"))?.user;



    const meetingUrl = useMemo(

        () => resolveMeetingsListUrl(currentUser?.userType, meetingInfo.userId),

        [currentUser?.userType, meetingInfo.userId]

    );



    const getUserMeetingDetails = async () => {

        const response = await getZoomData(meetingId);

        setMeetingDetails(response.data);

        const downloadUrl = response?.data?.recording[0]?.download_url;

        setVideoUrl(downloadUrl);

    };



    useEffect(() => {

        getUserMeetingDetails();

    }, []);



    const formattedRange = formatMeetingDateTimeDisplay(meetingInfo);



    const handleDownloadSummary = () => {

        const summary = meetingDetails?.chatAndTranscript?.summary;

        if (!summary) {

            enqueueSnackbar("No summary available to download.");

            return;

        }

        triggerTextFileDownload(summary, "meeting-summary.txt");

    };



    const handleExpandClick = () => {

        requestElementFullscreen(notesRef.current);

    };



    if (!meetingDetails) {

        return <Loader />;

    }



    return (
        <Page data-slot="meeting-detail">
            <PageHead
                title="Meetings"
                subtitle="Your complete log of past, present, and upcoming meetings."
            />
            <div data-slot="meeting-detail-header" className="tw-mb-6 tw-flex tw-items-center tw-gap-3">
                <Button as={NavLink} to={meetingUrl} variant="icon" iconOnly aria-label="Back to meetings" data-slot="common-back-btn">
                    <StarsIcon name="chevronLeft" className="tw-h-5 tw-w-5 tw-shrink-0" />
                </Button>
                <h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">{formattedRange}</h2>
            </div>

            <div className="tw-grid tw-gap-6 lg:tw-grid-cols-3">
                <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-6 lg:tw-col-span-2">
                    <div data-slot="meeting-vdo" className="tw-overflow-hidden tw-rounded-lg tw-bg-overlay-navy/70">
                        <video className="tw-block tw-w-full" src={videoUrl} controls loop muted></video>
                    </div>
                    <Panel>
                        <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-pb-4">
                            <h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">Notes</h2>
                            <div className="tw-flex tw-gap-2">
                            <Button variant="icon" iconOnly aria-label="Download the summary" data-slot="download" onClick={handleDownloadSummary}>
                                <StarsIcon name="download" className="tw-h-5 tw-w-5 tw-shrink-0" />
                            </Button>
                            <Button variant="icon" iconOnly aria-label="Show the summary full screen" data-slot="expand" onClick={handleExpandClick}>
                                <StarsIcon name="maximize" className="tw-h-5 tw-w-5 tw-shrink-0" />
                            </Button>
                            </div>
                        </div>
                        <div ref={notesRef} data-slot="meeting-notes" className="tw-bg-surface tw-text-text">
                            <p className="tw-m-0 tw-whitespace-pre-wrap tw-text-md tw-text-text">{meetingDetails?.chatAndTranscript?.summary || "No summary available."}</p>
                        </div>
                    </Panel>
                </div>

                <Panel title="Transcript" data-slot="transcript-sticky" className="lg:tw-sticky lg:tw-top-6 lg:tw-self-start">
                    <div data-slot="transcript-box-list" className="tw-max-h-screen tw-overflow-y-auto tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-4">
                        {meetingDetails?.chatAndTranscript?.transcript?.transcription?.length > 0 ? (
                            meetingDetails.chatAndTranscript.transcript.transcription.map((item, index) => (
                                <div key={item?._id} data-slot="single-transcript-box" className="tw-mb-4 tw-flex tw-gap-3">
                                    <div
                                        data-slot="profile-pic"
                                        className={cn(
                                            "tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-pill tw-text-xs tw-font-bold",
                                            index % 2 === 0 ? "tw-bg-action tw-text-action-text" : "tw-bg-accent-tint tw-text-text-accent",
                                        )}
                                    >
                                        <h3 className="tw-m-0 tw-text-xs tw-font-bold tw-text-inherit">{item.speaker?.split(" ").map((name) => name[0]).join("")}</h3>
                                    </div>
                                    <div data-slot="transcript-msg" className="tw-min-w-0">
                                        <p className="tw-m-0 tw-text-sm tw-text-text">{item.message}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="tw-m-0 tw-text-sm tw-text-text-muted">No transcript available.</p>
                        )}
                    </div>
                </Panel>
            </div>
        </Page>
    );

};



export default MeetingDetails;

